import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { getBankMedia } from '../../requests/documentDatas';
import { normalizeBankMedia } from '../../shared/media/mediaBank';
import {
  getMediaBankSnapshot,
  getMediaBankSource,
  subscribeMediaBank,
} from './mediaBankRegistry';

/**
 * Paliers d'un média déjà posé.
 *
 * Un bloc ne persiste que l'URL retenue et l'id du média : rouvrir le sélecteur
 * de résolution demande de retrouver `variants`. On regarde d'abord la banque
 * déjà chargée — l'utilisateur vient souvent d'y choisir le média — et on ne
 * tombe sur la requête unitaire que si elle n'est pas en mémoire. Charger toute
 * la banque pour un seul média serait payer bien plus que ce qu'on cherche.
 */

const cache = new Map();

function findInFolders(folders, mediaId) {
  for (const folder of folders) {
    const found = folder.media.find((media) => String(media.id) === String(mediaId));
    if (found) return found;
  }
  return null;
}

export function useBankMedia(mediaId) {
  /* La banque peut arriver après ce champ — l'utilisateur ouvre l'onglet
     « Banque » alors que le sélecteur de résolution est déjà monté. S'y abonner
     évite de refetcher un média qu'on vient de recevoir. */
  const { folders } = useSyncExternalStore(subscribeMediaBank, getMediaBankSnapshot);
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);

  const known = useMemo(
    () => (mediaId ? findInFolders(folders, mediaId) || cache.get(String(mediaId)) || null : null),
    [folders, mediaId],
  );

  useEffect(() => {
    if (!mediaId || known) {
      setFetched(null);
      setLoading(false);
      return undefined;
    }

    const source = getMediaBankSource();
    if (!source) return undefined;

    let cancelled = false;
    setLoading(true);
    getBankMedia({ ...source, mediaId })
      .then((raw) => {
        if (cancelled) return;
        const normalized = normalizeBankMedia(raw);
        if (normalized) cache.set(String(mediaId), normalized);
        setFetched(normalized);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [mediaId, known]);

  return { media: known || fetched, loading };
}

/** Le média fraîchement uploadé est connu sans requête. */
export function rememberBankMedia(media) {
  if (media?.id != null) cache.set(String(media.id), media);
}

/** Remise à zéro — réservée aux tests. */
export function resetBankMediaCache() {
  cache.clear();
}
